<template>
  <div class="pd">

    <!-- ── Header ───────────────────────────────────── -->
    <div class="pd-header">
      <div>
        <h4 class="pd-title">Procurement Dashboard</h4>
        <p class="pd-sub">Live overview of requisitions, orders, vendors &amp; spend</p>
      </div>
      <button class="pd-refresh" @click="load" :disabled="loading" title="Refresh">
        <font-awesome-icon icon="sync-alt" :class="{ 'fa-spin': loading }" />
      </button>
    </div>

    <!-- ── KPI Cards ─────────────────────────────────── -->
    <div class="kpi-grid">
      <template v-if="loading">
        <div class="kpi-card" v-for="n in 8" :key="n">
          <div class="skeleton" style="width:36px;height:36px;border-radius:10px"></div>
          <div style="flex:1">
            <div class="skeleton" style="height:12px;width:60%;border-radius:4px"></div>
            <div class="skeleton mt-6" style="height:22px;width:45%;border-radius:4px"></div>
          </div>
        </div>
      </template>
      <template v-else>
        <div class="kpi-card" @click="go('Purchase Requisitions')">
          <div class="kpi-icon kpi-icon--blue"><font-awesome-icon icon="clipboard-list" /></div>
          <div>
            <p class="kpi-label">Total PRs</p>
            <p class="kpi-value">{{ kpis.total_prs }}</p>
            <p class="kpi-sub" v-if="kpis.pending_prs">{{ kpis.pending_prs }} pending approval</p>
          </div>
        </div>
        <div class="kpi-card" @click="go('Purchase Orders')">
          <div class="kpi-icon kpi-icon--indigo"><font-awesome-icon icon="file-alt" /></div>
          <div>
            <p class="kpi-label">Total POs</p>
            <p class="kpi-value">{{ kpis.total_pos }}</p>
            <p class="kpi-sub" v-if="kpis.active_pos">{{ kpis.active_pos }} active</p>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon kpi-icon--green"><font-awesome-icon icon="coins" /></div>
          <div>
            <p class="kpi-label">Total PO Value</p>
            <p class="kpi-value">{{ fmt(kpis.total_po_value) }}</p>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon kpi-icon--red"><font-awesome-icon icon="hand-holding-usd" /></div>
          <div>
            <p class="kpi-label">Outstanding Payables</p>
            <p class="kpi-value kpi-value--red">{{ fmt(kpis.outstanding_value) }}</p>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon kpi-icon--green"><font-awesome-icon icon="check-circle" /></div>
          <div>
            <p class="kpi-label">Amount Paid</p>
            <p class="kpi-value kpi-value--green">{{ fmt(kpis.paid_po_value) }}</p>
          </div>
        </div>
        <div class="kpi-card" @click="go('Vendor Directory')">
          <div class="kpi-icon kpi-icon--teal"><font-awesome-icon icon="store" /></div>
          <div>
            <p class="kpi-label">Active Vendors</p>
            <p class="kpi-value">{{ kpis.active_vendors }}</p>
          </div>
        </div>
        <div class="kpi-card" @click="go('Sourcing Events')">
          <div class="kpi-icon kpi-icon--orange"><font-awesome-icon icon="bullhorn" /></div>
          <div>
            <p class="kpi-label">Active Sourcing Events</p>
            <p class="kpi-value">{{ kpis.active_sourcing }}</p>
          </div>
        </div>
        <div class="kpi-card">
          <div class="kpi-icon kpi-icon--purple"><font-awesome-icon icon="truck-loading" /></div>
          <div>
            <p class="kpi-label">GRNs This Month</p>
            <p class="kpi-value">{{ kpis.grns_this_month }}</p>
          </div>
        </div>
      </template>
    </div>

    <!-- ── Spend Trend + Payment Status ──────────────── -->
    <div class="row-2">

      <!-- Spend Trend -->
      <div class="dash-card dash-card--wide">
        <div class="card-header">
          <div class="card-title-block">
            <div class="card-icon card-icon--blue"><font-awesome-icon icon="chart-line" /></div>
            <div>
              <p class="card-title">Monthly Spend Trend</p>
              <p class="card-sub">Issued PO value over last 12 months</p>
            </div>
          </div>
        </div>
        <div v-if="loading" class="skeleton" style="height:200px;border-radius:8px"></div>
        <div v-else-if="!spendTrend.length" class="empty-state">No issued POs yet</div>
        <div v-else class="chart-wrap">
          <div class="bar-chart">
            <div v-for="d in spendTrend" :key="d.month" class="bar-col">
              <div class="bar-tip">{{ fmtShort(d.total) }}</div>
              <div class="bar" :style="{ height: barHeight(d.total) + '%' }" :title="`${d.month}: ${fmt(d.total)}`"></div>
              <div class="bar-label">{{ shortMonth(d.month) }}</div>
            </div>
          </div>
        </div>
      </div>

      <!-- Payment Obligations -->
      <div class="dash-card">
        <div class="card-header">
          <div class="card-title-block">
            <div class="card-icon card-icon--orange"><font-awesome-icon icon="file-invoice-dollar" /></div>
            <div>
              <p class="card-title">Payment Obligations</p>
              <p class="card-sub">Schedule status</p>
            </div>
          </div>
        </div>
        <div v-if="loading" class="skeleton" style="height:160px;border-radius:8px"></div>
        <div v-else class="pay-stack">
          <div class="pay-row pay-row--red">
            <div class="pay-dot pay-dot--red"></div>
            <span class="pay-label">Overdue</span>
            <span class="pay-amount">{{ fmt(paymentStatus.overdue) }}</span>
          </div>
          <div class="pay-row pay-row--yellow">
            <div class="pay-dot pay-dot--yellow"></div>
            <span class="pay-label">Due Now</span>
            <span class="pay-amount">{{ fmt(paymentStatus.due_today) }}</span>
          </div>
          <div class="pay-row pay-row--blue">
            <div class="pay-dot pay-dot--blue"></div>
            <span class="pay-label">Next 30 Days</span>
            <span class="pay-amount">{{ fmt(paymentStatus.upcoming_30) }}</span>
          </div>
          <div class="pay-row pay-row--gray">
            <div class="pay-dot pay-dot--gray"></div>
            <span class="pay-label">Future</span>
            <span class="pay-amount">{{ fmt(paymentStatus.future) }}</span>
          </div>
        </div>
      </div>

    </div>

    <!-- ── Status Breakdowns ───────────────────────── -->
    <div class="row-2">

      <!-- PR Status -->
      <div class="dash-card">
        <div class="card-header">
          <div class="card-title-block">
            <div class="card-icon card-icon--indigo"><font-awesome-icon icon="clipboard-check" /></div>
            <div>
              <p class="card-title">Requisition Status</p>
              <p class="card-sub">Distribution by stage</p>
            </div>
          </div>
          <button class="card-link" @click="go('Purchase Requisitions')">View all</button>
        </div>
        <div v-if="loading" class="skeleton" style="height:140px;border-radius:8px"></div>
        <div v-else class="status-bars">
          <div v-for="(count, status) in prBreakdown" :key="status" class="status-bar-row">
            <span class="status-bar-label">{{ formatStatus(status) }}</span>
            <div class="status-bar-track">
              <div class="status-bar-fill" :class="`status-fill--${prStatusColor(status)}`"
                   :style="{ width: pct(count, totalPrs) + '%' }"></div>
            </div>
            <span class="status-bar-count">{{ count }}</span>
          </div>
          <div v-if="!Object.keys(prBreakdown).length" class="empty-state">No requisitions yet</div>
        </div>
      </div>

      <!-- PO Status -->
      <div class="dash-card">
        <div class="card-header">
          <div class="card-title-block">
            <div class="card-icon card-icon--teal"><font-awesome-icon icon="shopping-cart" /></div>
            <div>
              <p class="card-title">Purchase Order Status</p>
              <p class="card-sub">Distribution by stage</p>
            </div>
          </div>
          <button class="card-link" @click="go('Purchase Orders')">View all</button>
        </div>
        <div v-if="loading" class="skeleton" style="height:140px;border-radius:8px"></div>
        <div v-else class="status-bars">
          <div v-for="(count, status) in poBreakdown" :key="status" class="status-bar-row">
            <span class="status-bar-label">{{ formatStatus(status) }}</span>
            <div class="status-bar-track">
              <div class="status-bar-fill" :class="`status-fill--${poStatusColor(status)}`"
                   :style="{ width: pct(count, totalPos) + '%' }"></div>
            </div>
            <span class="status-bar-count">{{ count }}</span>
          </div>
          <div v-if="!Object.keys(poBreakdown).length" class="empty-state">No purchase orders yet</div>
        </div>
      </div>

    </div>

    <!-- ── Top Vendors ────────────────────────────── -->
    <div class="dash-card">
      <div class="card-header">
        <div class="card-title-block">
          <div class="card-icon card-icon--green"><font-awesome-icon icon="medal" /></div>
          <div>
            <p class="card-title">Top Vendors by Spend</p>
            <p class="card-sub">Based on issued purchase orders</p>
          </div>
        </div>
        <button class="card-link" @click="go('Vendor Directory')">View all</button>
      </div>
      <div v-if="loading" class="skeleton" style="height:120px;border-radius:8px"></div>
      <div v-else-if="!topVendors.length" class="empty-state">No vendor spend data yet</div>
      <div v-else class="vendor-bars">
        <div v-for="(v, i) in topVendors" :key="v.id" class="vendor-bar-row">
          <span class="vendor-rank">#{{ i + 1 }}</span>
          <span class="vendor-name">{{ v.name }}</span>
          <div class="vendor-bar-track">
            <div class="vendor-bar-fill" :style="{ width: pct(v.spend, topVendors[0].spend) + '%' }"></div>
          </div>
          <span class="vendor-spend">{{ fmt(v.spend) }}</span>
          <span class="vendor-po-count">{{ v.po_count }} PO{{ v.po_count !== 1 ? 's' : '' }}</span>
        </div>
      </div>
    </div>

    <!-- ── Recent POs + GRN Pending ────────────────── -->
    <div class="row-2">

      <!-- Recent POs -->
      <div class="dash-card">
        <div class="card-header">
          <div class="card-title-block">
            <div class="card-icon card-icon--indigo"><font-awesome-icon icon="file-alt" /></div>
            <div>
              <p class="card-title">Recent Purchase Orders</p>
              <p class="card-sub">Latest 8 orders</p>
            </div>
          </div>
          <button class="card-link" @click="go('Purchase Orders')">View all</button>
        </div>
        <div v-if="loading" class="skeleton" style="height:180px;border-radius:8px"></div>
        <div v-else-if="!recentPos.length" class="empty-state">No purchase orders yet</div>
        <div v-else class="mini-table-wrap">
          <table class="mini-table">
            <thead><tr><th>PO #</th><th>Vendor</th><th>Value</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="po in recentPos" :key="po.id" class="mini-row" @click="goPo(po.id)">
                <td class="mono">{{ po.po_number }}</td>
                <td>{{ po.vendor }}</td>
                <td class="num">{{ po.currency }} {{ fmt(po.total) }}</td>
                <td><span class="s-chip" :class="`s-chip--${po.status}`">{{ formatStatus(po.status) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- GRN Pending -->
      <div class="dash-card">
        <div class="card-header">
          <div class="card-title-block">
            <div class="card-icon card-icon--orange"><font-awesome-icon icon="truck" /></div>
            <div>
              <p class="card-title">Awaiting Delivery</p>
              <p class="card-sub">Issued POs pending full receipt</p>
            </div>
          </div>
        </div>
        <div v-if="loading" class="skeleton" style="height:180px;border-radius:8px"></div>
        <div v-else-if="!grnPending.length" class="empty-state">
          <font-awesome-icon icon="check-circle" class="empty-icon empty-icon--green" />
          All orders received
        </div>
        <div v-else class="mini-table-wrap">
          <table class="mini-table">
            <thead><tr><th>PO #</th><th>Vendor</th><th>Value</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="po in grnPending" :key="po.id" class="mini-row" @click="goPo(po.id)">
                <td class="mono">{{ po.po_number }}</td>
                <td>{{ po.vendor }}</td>
                <td class="num">{{ po.currency }} {{ fmt(po.total) }}</td>
                <td><span class="s-chip" :class="`s-chip--${po.status}`">{{ formatStatus(po.status) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

    </div>

    <!-- ── Recent PRs + Sourcing Events ─────────────── -->
    <div class="row-2">

      <!-- Recent PRs -->
      <div class="dash-card">
        <div class="card-header">
          <div class="card-title-block">
            <div class="card-icon card-icon--blue"><font-awesome-icon icon="clipboard-list" /></div>
            <div>
              <p class="card-title">Recent Requisitions</p>
              <p class="card-sub">Latest 8 PRs</p>
            </div>
          </div>
          <button class="card-link" @click="go('Purchase Requisitions')">View all</button>
        </div>
        <div v-if="loading" class="skeleton" style="height:180px;border-radius:8px"></div>
        <div v-else-if="!recentPrs.length" class="empty-state">No requisitions yet</div>
        <div v-else class="mini-table-wrap">
          <table class="mini-table">
            <thead><tr><th>PR #</th><th>Title</th><th>Urgency</th><th>Status</th></tr></thead>
            <tbody>
              <tr v-for="pr in recentPrs" :key="pr.id" class="mini-row" @click="goPr(pr.id)">
                <td class="mono">{{ pr.pr_number }}</td>
                <td class="truncate">{{ pr.title }}</td>
                <td><span v-if="pr.urgency" class="urgency-chip" :class="`urgency--${pr.urgency}`">{{ pr.urgency }}</span></td>
                <td><span class="s-chip" :class="`s-chip--${pr.status}`">{{ formatStatus(pr.status) }}</span></td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- Active Sourcing Events -->
      <div class="dash-card">
        <div class="card-header">
          <div class="card-title-block">
            <div class="card-icon card-icon--purple"><font-awesome-icon icon="bullhorn" /></div>
            <div>
              <p class="card-title">Sourcing Events</p>
              <p class="card-sub">Active RFQs / RFPs / RFIs</p>
            </div>
          </div>
          <button class="card-link" @click="go('Sourcing Events')">View all</button>
        </div>
        <div v-if="loading" class="skeleton" style="height:100px;border-radius:8px"></div>
        <div v-else>
          <!-- Status chips -->
          <div class="sourcing-chips">
            <div v-for="(count, status) in sourcingSummary.breakdown" :key="status" class="sourcing-chip">
              <span class="sourcing-chip-count">{{ count }}</span>
              <span class="sourcing-chip-label">{{ formatStatus(status) }}</span>
            </div>
          </div>
          <!-- Active events -->
          <div v-if="sourcingSummary.active?.length" class="sourcing-list">
            <div v-for="ev in sourcingSummary.active" :key="ev.id" class="sourcing-item" @click="goSe(ev.id)">
              <div class="sourcing-item-main">
                <span class="sourcing-type-badge">{{ ev.event_type }}</span>
                <span class="sourcing-item-title">{{ ev.title }}</span>
              </div>
              <div class="sourcing-item-meta" v-if="ev.submission_deadline">
                Deadline: {{ fmtDate(ev.submission_deadline) }}
                <span class="deadline-days" :class="deadlineClass(ev.submission_deadline)">{{ deadlineDays(ev.submission_deadline) }}</span>
              </div>
            </div>
          </div>
          <div v-else class="empty-state">No active sourcing events</div>
        </div>
      </div>

    </div>

  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import axios from 'axios'

const router  = useRouter()
const loading = ref(false)
const data    = ref({})

const kpis           = computed(() => data.value.kpis            ?? {})
const prBreakdown    = computed(() => data.value.pr_breakdown    ?? {})
const poBreakdown    = computed(() => data.value.po_breakdown    ?? {})
const spendTrend     = computed(() => data.value.spend_trend     ?? [])
const topVendors     = computed(() => data.value.top_vendors     ?? [])
const paymentStatus  = computed(() => data.value.payment_status  ?? {})
const recentPos      = computed(() => data.value.recent_pos      ?? [])
const recentPrs      = computed(() => data.value.recent_prs      ?? [])
const sourcingSummary = computed(() => data.value.sourcing_summary ?? { breakdown: {}, active: [] })
const grnPending     = computed(() => data.value.grn_pending     ?? [])

const totalPrs = computed(() => Object.values(prBreakdown.value).reduce((s, v) => s + v, 0))
const totalPos = computed(() => Object.values(poBreakdown.value).reduce((s, v) => s + v, 0))

const load = async () => {
  loading.value = true
  try {
    const res = await axios.get('/dashboard/procurement')
    data.value = res.data
  } catch (e) {
    console.error(e)
  } finally {
    loading.value = false
  }
}

const fmt = (v) => Number(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })
const fmtShort = (v) => {
  const n = Number(v || 0)
  if (n >= 1000000) return (n / 1000000).toFixed(1) + 'M'
  if (n >= 1000)    return (n / 1000).toFixed(1) + 'K'
  return n.toFixed(0)
}
const fmtDate = (d) => d ? new Date(d).toLocaleDateString(undefined, { day: '2-digit', month: 'short', year: 'numeric' }) : '—'
const pct = (val, total) => total > 0 ? Math.round((val / total) * 100) : 0

const barHeight = (v) => {
  const max = Math.max(...spendTrend.value.map(d => d.total), 1)
  return Math.max(4, Math.round((v / max) * 100))
}

const shortMonth = (ym) => {
  const [y, m] = ym.split('-')
  return new Date(+y, +m - 1).toLocaleDateString(undefined, { month: 'short' })
}

const formatStatus = (s) => ({
  draft: 'Draft', submitted: 'Submitted', pending_approval: 'Pending', approved: 'Approved',
  rejected: 'Rejected', ordered: 'Ordered', fulfilled: 'Fulfilled', cancelled: 'Cancelled',
  issued: 'Issued', partially_received: 'Part. Received', completed: 'Completed', closed: 'Closed',
  open: 'Open', awarded: 'Awarded', cancelled_event: 'Cancelled',
}[s] ?? s?.replace(/_/g, ' '))

const prStatusColor = (s) => ({ draft:'gray', submitted:'blue', pending_approval:'yellow', approved:'green', rejected:'red', ordered:'teal', fulfilled:'green', cancelled:'red' }[s] ?? 'gray')
const poStatusColor = (s) => ({ draft:'gray', issued:'blue', partially_received:'yellow', completed:'teal', closed:'green', cancelled:'red' }[s] ?? 'gray')

const deadlineDays = (d) => {
  const diff = Math.round((new Date(d) - new Date()) / 86400000)
  if (diff < 0)  return `${Math.abs(diff)}d overdue`
  if (diff === 0) return 'Today'
  return `${diff}d left`
}
const deadlineClass = (d) => {
  const diff = Math.round((new Date(d) - new Date()) / 86400000)
  if (diff < 0)  return 'dl--overdue'
  if (diff <= 3) return 'dl--soon'
  return 'dl--ok'
}

const go    = (name) => router.push({ name })
const goPo  = (id)   => router.push({ name: 'PO Detail', params: { id } })
const goPr  = (id)   => router.push({ name: 'PR Detail', params: { id } })
const goSe  = (id)   => router.push({ name: 'Sourcing Event Detail', params: { id } })

onMounted(load)
</script>

<style scoped>
.pd { padding: 24px; max-width: 1400px; margin: 0 auto; }
.pd-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:24px; }
.pd-title { font-size:22px; font-weight:700; color:#0f1f3d; margin:0; }
.pd-sub { font-size:13px; color:#7a90b0; margin:4px 0 0; }
.pd-refresh { background:none; border:1.5px solid #dde5f0; border-radius:8px; padding:8px 12px; color:#5a6a85; cursor:pointer; font-size:14px; transition:all .15s; }
.pd-refresh:hover { background:#f4f7fb; color:#004AAD; border-color:#004AAD; }

/* KPI */
.kpi-grid { display:grid; grid-template-columns:repeat(4,1fr); gap:14px; margin-bottom:20px; }
.kpi-card { background:#fff; border:1.5px solid #e8eef7; border-radius:12px; padding:16px; display:flex; align-items:flex-start; gap:14px; cursor:pointer; transition:box-shadow .15s, border-color .15s; }
.kpi-card:hover { box-shadow:0 4px 16px rgba(0,74,173,.1); border-color:#c5d5f0; }
.kpi-icon { width:40px; height:40px; border-radius:10px; display:flex; align-items:center; justify-content:center; font-size:16px; flex-shrink:0; }
.kpi-icon--blue    { background:#eff4ff; color:#3b82f6; }
.kpi-icon--indigo  { background:#eef0ff; color:#6366f1; }
.kpi-icon--green   { background:#effaf3; color:#16a34a; }
.kpi-icon--red     { background:#fff0f0; color:#dc2626; }
.kpi-icon--teal    { background:#effafa; color:#0d9488; }
.kpi-icon--orange  { background:#fff7ed; color:#ea580c; }
.kpi-icon--purple  { background:#f5f0ff; color:#7c3aed; }
.kpi-icon--blue2   { background:#eff6ff; color:#2563eb; }
.kpi-label { font-size:11.5px; color:#7a90b0; font-weight:500; margin:0; text-transform:uppercase; letter-spacing:.4px; }
.kpi-value { font-size:22px; font-weight:700; color:#0f1f3d; margin:2px 0 0; }
.kpi-value--red   { color:#dc2626; }
.kpi-value--green { color:#16a34a; }
.kpi-sub { font-size:11px; color:#ea580c; margin:2px 0 0; }

/* Layout */
.row-2 { display:grid; grid-template-columns:1fr 1fr; gap:16px; margin-bottom:16px; }
.dash-card { background:#fff; border:1.5px solid #e8eef7; border-radius:12px; padding:20px; margin-bottom:16px; }
.dash-card--wide { }

/* Card header */
.card-header { display:flex; align-items:center; justify-content:space-between; margin-bottom:16px; }
.card-title-block { display:flex; align-items:center; gap:12px; }
.card-icon { width:36px; height:36px; border-radius:9px; display:flex; align-items:center; justify-content:center; font-size:14px; }
.card-icon--blue    { background:#eff4ff; color:#3b82f6; }
.card-icon--indigo  { background:#eef0ff; color:#6366f1; }
.card-icon--green   { background:#effaf3; color:#16a34a; }
.card-icon--orange  { background:#fff7ed; color:#ea580c; }
.card-icon--teal    { background:#effafa; color:#0d9488; }
.card-icon--purple  { background:#f5f0ff; color:#7c3aed; }
.card-title { font-size:14px; font-weight:600; color:#1a2b45; margin:0; }
.card-sub   { font-size:12px; color:#7a90b0; margin:2px 0 0; }
.card-link  { background:none; border:none; color:#004AAD; font-size:12px; font-weight:500; cursor:pointer; padding:0; }
.card-link:hover { text-decoration:underline; }

/* Bar chart */
.chart-wrap { overflow-x:auto; padding-bottom:4px; }
.bar-chart { display:flex; align-items:flex-end; gap:8px; height:180px; padding:0 4px; }
.bar-col { display:flex; flex-direction:column; align-items:center; flex:1; min-width:32px; }
.bar-tip { font-size:10px; color:#7a90b0; margin-bottom:4px; white-space:nowrap; }
.bar { width:100%; background:linear-gradient(180deg,#3b82f6 0%,#6366f1 100%); border-radius:4px 4px 0 0; transition:opacity .15s; min-height:4px; }
.bar:hover { opacity:.8; }
.bar-label { font-size:10px; color:#7a90b0; margin-top:5px; }

/* Payment */
.pay-stack { display:flex; flex-direction:column; gap:10px; }
.pay-row { display:flex; align-items:center; gap:10px; padding:10px 12px; border-radius:8px; }
.pay-row--red    { background:#fff5f5; }
.pay-row--yellow { background:#fffbeb; }
.pay-row--blue   { background:#eff6ff; }
.pay-row--gray   { background:#f8fafc; }
.pay-dot { width:10px; height:10px; border-radius:50%; flex-shrink:0; }
.pay-dot--red    { background:#dc2626; }
.pay-dot--yellow { background:#d97706; }
.pay-dot--blue   { background:#3b82f6; }
.pay-dot--gray   { background:#94a3b8; }
.pay-label { flex:1; font-size:13px; color:#374151; font-weight:500; }
.pay-amount { font-size:13px; font-weight:700; color:#0f1f3d; }

/* Status bars */
.status-bars { display:flex; flex-direction:column; gap:8px; }
.status-bar-row { display:flex; align-items:center; gap:10px; }
.status-bar-label { font-size:12px; color:#5a6a85; width:110px; flex-shrink:0; }
.status-bar-track { flex:1; background:#f0f4fa; border-radius:4px; height:8px; overflow:hidden; }
.status-bar-fill  { height:100%; border-radius:4px; transition:width .4s; }
.status-bar-count { font-size:12px; font-weight:600; color:#1a2b45; width:24px; text-align:right; }
.status-fill--gray   { background:#94a3b8; }
.status-fill--blue   { background:#3b82f6; }
.status-fill--yellow { background:#f59e0b; }
.status-fill--green  { background:#22c55e; }
.status-fill--red    { background:#ef4444; }
.status-fill--teal   { background:#14b8a6; }

/* Top vendors */
.vendor-bars { display:flex; flex-direction:column; gap:10px; }
.vendor-bar-row { display:flex; align-items:center; gap:10px; }
.vendor-rank   { font-size:12px; color:#7a90b0; width:20px; text-align:center; font-weight:700; }
.vendor-name   { font-size:13px; color:#1a2b45; font-weight:500; width:160px; flex-shrink:0; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }
.vendor-bar-track { flex:1; background:#f0f4fa; border-radius:4px; height:8px; overflow:hidden; }
.vendor-bar-fill  { height:100%; background:linear-gradient(90deg,#3b82f6,#6366f1); border-radius:4px; }
.vendor-spend     { font-size:12px; font-weight:700; color:#0f1f3d; width:90px; text-align:right; }
.vendor-po-count  { font-size:11px; color:#7a90b0; width:50px; text-align:right; }

/* Mini table */
.mini-table-wrap { overflow-x:auto; }
.mini-table { width:100%; border-collapse:collapse; font-size:13px; }
.mini-table th { font-size:11px; text-transform:uppercase; color:#7a90b0; font-weight:600; padding:6px 8px; border-bottom:1.5px solid #f0f4fa; letter-spacing:.4px; }
.mini-table td { padding:8px 8px; border-bottom:1px solid #f8fafc; color:#1a2b45; vertical-align:middle; }
.mini-row { cursor:pointer; transition:background .1s; }
.mini-row:hover { background:#f8fafc; }
.mini-row:last-child td { border-bottom:none; }
.num { text-align:right; font-variant-numeric:tabular-nums; }
.mono { font-family:monospace; font-size:12px; }
.truncate { max-width:160px; overflow:hidden; text-overflow:ellipsis; white-space:nowrap; }

/* Status chips */
.s-chip { display:inline-block; padding:2px 8px; border-radius:20px; font-size:11px; font-weight:600; white-space:nowrap; }
.s-chip--draft              { background:#f0f4fa; color:#5a6a85; }
.s-chip--submitted          { background:#eff4ff; color:#3b82f6; }
.s-chip--pending_approval   { background:#fffbeb; color:#d97706; }
.s-chip--approved           { background:#effaf3; color:#16a34a; }
.s-chip--rejected           { background:#fff0f0; color:#dc2626; }
.s-chip--ordered            { background:#effafa; color:#0d9488; }
.s-chip--fulfilled          { background:#effaf3; color:#16a34a; }
.s-chip--cancelled          { background:#fff0f0; color:#dc2626; }
.s-chip--issued             { background:#eff4ff; color:#3b82f6; }
.s-chip--partially_received { background:#fffbeb; color:#d97706; }
.s-chip--completed          { background:#effafa; color:#0d9488; }
.s-chip--closed             { background:#effaf3; color:#16a34a; }

/* Urgency */
.urgency-chip { display:inline-block; padding:2px 7px; border-radius:20px; font-size:10px; font-weight:700; text-transform:uppercase; }
.urgency--low    { background:#effaf3; color:#16a34a; }
.urgency--medium { background:#fffbeb; color:#d97706; }
.urgency--high   { background:#fff0f0; color:#dc2626; }
.urgency--urgent { background:#fff0f0; color:#dc2626; }

/* Sourcing */
.sourcing-chips { display:flex; flex-wrap:wrap; gap:8px; margin-bottom:14px; }
.sourcing-chip  { background:#f4f7fb; border-radius:8px; padding:8px 14px; text-align:center; min-width:72px; }
.sourcing-chip-count { display:block; font-size:20px; font-weight:700; color:#1a2b45; }
.sourcing-chip-label { display:block; font-size:11px; color:#7a90b0; margin-top:2px; }
.sourcing-list { display:flex; flex-direction:column; gap:8px; }
.sourcing-item { border:1.5px solid #f0f4fa; border-radius:8px; padding:10px 12px; cursor:pointer; transition:border-color .15s; }
.sourcing-item:hover { border-color:#c5d5f0; background:#f8fafc; }
.sourcing-item-main { display:flex; align-items:center; gap:8px; margin-bottom:4px; }
.sourcing-type-badge { background:#eef0ff; color:#6366f1; font-size:10px; font-weight:700; padding:2px 7px; border-radius:20px; text-transform:uppercase; }
.sourcing-item-title { font-size:13px; color:#1a2b45; font-weight:500; }
.sourcing-item-meta { font-size:11px; color:#7a90b0; display:flex; align-items:center; gap:6px; }
.deadline-days { font-weight:600; }
.dl--overdue { color:#dc2626; }
.dl--soon    { color:#d97706; }
.dl--ok      { color:#16a34a; }

/* Empty */
.empty-state { text-align:center; padding:28px 16px; font-size:13px; color:#94a3b8; }
.empty-icon  { font-size:24px; display:block; margin:0 auto 8px; }
.empty-icon--green { color:#22c55e; }

/* Skeleton */
.skeleton { background:linear-gradient(90deg,#f0f4fa 25%,#e8eef7 50%,#f0f4fa 75%); background-size:200% 100%; animation:shimmer 1.4s infinite; border-radius:6px; }
.mt-6 { margin-top:6px; }
@keyframes shimmer { 0%{background-position:200% 0} 100%{background-position:-200% 0} }

/* Responsive */
@media (max-width: 1100px) {
  .kpi-grid { grid-template-columns:repeat(2, 1fr); }
}
@media (max-width: 720px) {
  .kpi-grid { grid-template-columns:1fr 1fr; }
  .row-2 { grid-template-columns:1fr; }
  .pd { padding:14px; }
}
</style>
